import React, { useState, useRef, useEffect } from 'react';
import CategoryAutocomplete from './CategoryAutocomplete';
import { crawlCategoryProducts, getCrawlingErrorMessage } from '../utils/crawlerUtils';

const AddProductModal = ({ 
    isOpen, 
    selectedSite, 
    onClose, 
    onBulkAdd, 
    onSingleAdd 
}) => {
    const [showUrlInput, setShowUrlInput] = useState(false);
    const [showCategorySearch, setShowCategorySearch] = useState(false);
    const [url, setUrl] = useState('');
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [modalSelectedSite, setModalSelectedSite] = useState(selectedSite || '');

    // URL 입력 필드에 대한 ref
    const urlInputRef = useRef(null);

    // 선택된 사이트 (모달 내부에서 선택한 사이트 또는 props로 받은 사이트)
    const currentSite = modalSelectedSite || selectedSite;

    // 카테고리 일괄 추가가 있는 사이트인지 확인
    const hasBulkAdd = currentSite === '올리브영';

    // 사이트 목록
    const sites = ['올리브영', '위버스', '무신사', '다이소', '국립박물관', '케이타운'];

    // 모달이 열릴 때 selectedSite가 없으면 초기화
    useEffect(() => {
        if (isOpen) {
            if (!selectedSite) {
                setModalSelectedSite('');
                setShowUrlInput(false);
                setShowCategorySearch(false);
            } else {
                setModalSelectedSite(selectedSite);
            }
        }
    }, [isOpen, selectedSite]);

    // 모달이 열릴 때 '단건 추가'만 있는 사이트면 자동으로 URL 입력 필드 표시
    useEffect(() => {
        if (isOpen && currentSite && !hasBulkAdd) {
            setShowUrlInput(true);
            setShowCategorySearch(false);
        } else if (isOpen && currentSite && hasBulkAdd) {
            // 카테고리 일괄 추가가 있는 사이트는 초기 상태로 리셋
            setShowUrlInput(false);
            setShowCategorySearch(false);
        }
    }, [isOpen, currentSite, hasBulkAdd]);

    // 사이트 선택 핸들러
    const handleSiteSelect = (site) => {
        setModalSelectedSite(site);
        // 사이트 선택 시 URL 입력 필드 활성화
        if (site && site !== '올리브영') {
            setShowUrlInput(true);
            setShowCategorySearch(false);
        } else if (site === '올리브영') {
            setShowUrlInput(false);
            setShowCategorySearch(false);
        }
    };

    // URL 입력 필드가 나타날 때 자동으로 포커싱
    useEffect(() => {
        if (showUrlInput && urlInputRef.current) {
            // 약간의 지연을 두어 DOM이 완전히 렌더링된 후 포커싱
            setTimeout(() => {
                urlInputRef.current.focus();
            }, 100);
        }
    }, [showUrlInput]);

    const handleSingleAddClick = () => {
        setShowUrlInput(true);
        setShowCategorySearch(false);
    };

    const handleBulkAddClick = () => {
        setShowCategorySearch(true);
        setShowUrlInput(false);
    };

    const handleCategorySelect = (category) => {
        setSelectedCategory(category);
    };

    const handleCategoryReset = () => {
        setSelectedCategory(null);
    };

    const handleCategoryDropdownClick = () => {
        // 카테고리 드롭다운이 클릭되면 다른 드롭다운들을 닫음
        setShowUrlInput(false);
    };

    const handleBulkAddSubmit = async () => {
        if (!selectedCategory || !currentSite) {
            alert('카테고리와 사이트를 선택해주세요.');
            return;
        }

        setIsLoading(true);
        try {
            const response = await crawlCategoryProducts(selectedCategory, currentSite);
            alert(response.message);
            if (selectedCategory) {
                onBulkAdd(selectedCategory);
            }
        } catch (error) {
            console.error('크롤링 에러:', error);
            alert(getCrawlingErrorMessage(error));
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async () => {
        if (url.trim() && currentSite) {
        setIsLoading(true);
        try {
            await onSingleAdd(url, currentSite);
            setUrl('');
            setShowUrlInput(false);
        } catch (error) {
            console.error('Error in handleSubmit:', error);
        } finally {
            setIsLoading(false);
        }
        }
    };

const handleClose = () => {
    setShowUrlInput(false);
    setShowCategorySearch(false);
    setUrl('');
    setSelectedCategory(null);
    setIsLoading(false);
    setModalSelectedSite('');
    onClose();
};

if (!isOpen) return null;

return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={handleClose}>
    <div className={`bg-white rounded-lg p-8 relative max-w-md w-full`} onClick={(e) => e.stopPropagation()}>
        {/* 닫기 버튼 */}
        <button 
        onClick={handleClose}
        className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-2xl"
        >
        ×
        </button>
        

        <h3 className="text-xl font-semibold text-gray-800 mb-6 text-center">
        {currentSite ? `${currentSite} 제품 추가` : '제품 추가'}
        </h3>
        
        {/* 사이트 선택 드롭다운 (selectedSite가 없을 때만 표시) */}
        {!selectedSite && (
            <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    사이트 선택
                </label>
                <select
                    value={modalSelectedSite}
                    onChange={(e) => handleSiteSelect(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-gray-500"
                >
                    <option value="">사이트를 선택하세요</option>
                    {sites.map((site) => (
                        <option key={site} value={site}>
                            {site}
                        </option>
                    ))}
                </select>
            </div>
        )}

        {/* 카테고리 일괄 추가가 있는 경우에만 버튼 표시 */}
        {currentSite && hasBulkAdd && (
            <div className="flex space-x-3 justify-center">
                {currentSite === '올리브영' && (
                    <button 
                        onClick={handleBulkAddClick}
                        className={`w-[150px] text-white py-3 px-4 rounded-lg text-sm font-medium transition-colors ${
                            showCategorySearch 
                                ? 'bg-slate-700' 
                                : 'bg-slate-500/60 hover:bg-slate-700'
                        }`}
                    >
                        카테고리 일괄 추가
                    </button>
                )}
                
                
                <button 
                    onClick={handleSingleAddClick}
                    className={`w-[150px] text-white py-3 px-4 rounded-lg text-sm font-medium transition-colors ${
                        showUrlInput 
                            ? 'bg-slate-700' 
                            : 'bg-slate-500/60 hover:bg-slate-700'
                    }`}
                >
                    단건 추가
                </button>
            </div>
        )}

        {currentSite && showCategorySearch && (
        <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="h-[200px]"> 
                <label className="block text-sm font-medium text-gray-700 mb-2">
                카테고리 선택
                </label>
                <CategoryAutocomplete
                    selectedSite={currentSite}
                    onCategorySelect={handleCategorySelect}
                    onDropdownClick={handleCategoryDropdownClick}
                    onReset={handleCategoryReset}
                    placeholder="카테고리명을 입력하세요 (예: 세럼)"
                    debounceMs={250}
                    />
            </div>
            <div className="flex space-x-3 mt-4">
                <button
                    onClick={handleBulkAddSubmit}
                    disabled={!selectedCategory || isLoading}
                    className="flex-1 bg-slate-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white py-2 px-4 rounded-md font-medium transition-colors flex items-center justify-center"
                >
                    {isLoading ? (
                    <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        처리 중...
                    </>
                    ) : (
                    '일괄 추가'
                    )}
                </button>
            </div>
        </div>
        )}

        {currentSite && showUrlInput && (
        <div className={`${hasBulkAdd ? 'mt-6 pt-6 border-t border-gray-200' : 'mt-0'} flex flex-col justify-center items-center`}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
            제품 URL을 입력하세요
            </label>
            <input
                ref={urlInputRef}
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyPress={(e) => {
                    if (e.key === 'Enter' && url.trim() && !isLoading) {
                        handleSubmit();
                    }
                }}
                placeholder="https://example.com/product"
                className="w-[100%] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-gray-500 mb-4"
            />
            <div className="flex space-x-3 justify-center">
                <button
                    onClick={handleSubmit}
                    disabled={!url.trim() || isLoading}
                    className="w-[150px] text-white py-3 px-4 rounded-lg text-sm font-medium transition-colors bg-slate-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center"
                >
                    {isLoading ? (
                    <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        처리 중...
                    </>
                    ) : (
                    '추가'
                    )}
                </button>
            </div>
        </div>
        )}
    </div>
    </div>
);
};

export default AddProductModal;
